import {
  OPERATION_WARNING_STATES,
  getOperationState,
  resolveOperationFailureReason,
  resolveOperationStatus,
} from "@src/cloud-connect/utils/resolve-operation-status";
import { queryGatewayOperationStatusUseCase } from "@src/cloud-connect/client-configuration/use-cases/query-gateway-operation-status-use-case";
import {
  DetectedMachineSkill,
  OPERATION_POLL_MAX_ATTEMPTS,
  resolveOperationErrorDetails,
  waitForNextOperationPoll,
} from "@src/cloud-connect/utils/onboarding-operation";
import { GatewayOperationError } from "@src/types/gateway-onboarding";
import { DeviceAuthOption } from "@src/types/device-auth-option";
import { CreateSkillRequestBody, SkillType } from "@src/types/skill-types";
import { OpcUaSettingsState } from "./use-add-new-machine-drawer-modal-texts";

const normalizeDetectedSkillPort = (
  rawPort: number | number[] | undefined,
): number | undefined => {
  if (typeof rawPort === "number") {
    return rawPort;
  }

  if (Array.isArray(rawPort)) {
    return rawPort.find((port) => Number.isInteger(port));
  }

  return undefined;
};

const normalizeDetectedSkillType = (rawType: string): SkillType | null => {
  const normalizedType = rawType.toUpperCase();

  if (normalizedType === "OPCUA") {
    return "OPCUA";
  }

  if (normalizedType === "VNC" || normalizedType === "RPC") {
    return normalizedType;
  }

  return null;
};

export interface MachineTexts {
  operationTimedOutErrorMessage: string;
  operationBackendFailureErrorMessage: string;
  operationFailureErrorMessage: string;
  operationPendingWarningMessage: string;
  machineCreationTimeoutWarningMessage?: string;
  skillsNotAvailableErrorMessage: string;
  createMachineFailedErrorMessage?: string;
}

export const isValidPort = (portValue: string): boolean => {
  const normalizedPortValue = portValue.trim();

  if (!/^\d{1,5}$/.test(normalizedPortValue)) {
    return false;
  }

  const port = Number(normalizedPortValue);
  return port >= 1 && port <= 65535;
};

const parseSkillsFromOperationResult = (
  skills: Record<string, number | number[] | undefined> | undefined,
): DetectedMachineSkill[] => {
  if (!skills) {
    return [];
  }

  const parsedSkills: DetectedMachineSkill[] = [];

  Object.entries(skills).forEach(([type, port]) => {
    const normalizedType = normalizeDetectedSkillType(type);
    if (!normalizedType) {
      return;
    }

    parsedSkills.push({
      type: normalizedType,
      port: normalizeDetectedSkillPort(port),
    });
  });

  return parsedSkills;
};

const getOperationOutcomeMessage = (
  state: string,
  fallback: string,
  texts: MachineTexts,
): string => {
  if (state === "TIMED_OUT") {
    return texts.operationTimedOutErrorMessage;
  }

  if (state === "BACKEND_FAILURE") {
    return texts.operationBackendFailureErrorMessage;
  }

  if (state === "FAILURE" || state === "FAILED" || state === "ERROR") {
    return texts.operationFailureErrorMessage;
  }

  if (OPERATION_WARNING_STATES.includes(state)) {
    return texts.operationPendingWarningMessage;
  }

  return fallback;
};

export const pollMachineAvailabilityOperation = async (
  operationId: string,
  texts: MachineTexts,
  signal?: AbortSignal,
): Promise<
  | { success: true; skills: DetectedMachineSkill[] }
  | {
      success: false;
      error: string;
      isWarning: boolean;
      operationId: string;
      details?: GatewayOperationError;
    }
> => {
  let lastState = "";
  let lastDetail = "";

  for (let attempt = 0; attempt < OPERATION_POLL_MAX_ATTEMPTS; attempt++) {
    const operationResult = await queryGatewayOperationStatusUseCase(
      operationId,
      signal,
    );

    if (!operationResult.success) {
      const details = resolveOperationErrorDetails(
        undefined,
        operationResult.errorData,
      );
      return {
        success: false,
        isWarning: false,
        operationId,
        details,
        error:
          details?.title ||
          operationResult.error ||
          texts.skillsNotAvailableErrorMessage,
      };
    }

    lastState = getOperationState(operationResult.data);
    lastDetail =
      operationResult.data?.message ||
      operationResult.data?.detail ||
      operationResult.error ||
      "";

    const operationStatus = resolveOperationStatus(operationResult.data);
    if (operationStatus === "success") {
      return {
        success: true,
        skills: parseSkillsFromOperationResult(
          operationResult.data?.result?.skills || operationResult.data?.skills,
        ),
      };
    }

    if (operationStatus === "error") {
      const failureReason = resolveOperationFailureReason(operationResult.data);
      const details = resolveOperationErrorDetails(operationResult.data);
      return {
        success: false,
        isWarning: false,
        operationId,
        details,
        error:
          failureReason ||
          lastDetail ||
          getOperationOutcomeMessage(
            lastState,
            texts.skillsNotAvailableErrorMessage,
            texts,
          ),
      };
    }

    if (attempt < OPERATION_POLL_MAX_ATTEMPTS - 1) {
      await waitForNextOperationPoll(signal);
    }
  }

  const isWarning = OPERATION_WARNING_STATES.includes(lastState);
  return {
    success: false,
    isWarning,
    operationId,
    error: getOperationOutcomeMessage(
      lastState,
      texts.skillsNotAvailableErrorMessage,
      texts,
    ),
  };
};

export const pollMachineCreationOperation = async (
  operationId: string,
  texts: MachineTexts,
  signal?: AbortSignal,
): Promise<
  | { success: true; machineId: string }
  | {
      success: false;
      error: string;
      isWarning: boolean;
      operationId: string;
      details?: GatewayOperationError;
    }
> => {
  let lastState = "";

  for (let attempt = 0; attempt < OPERATION_POLL_MAX_ATTEMPTS; attempt++) {
    const operationResult = await queryGatewayOperationStatusUseCase(
      operationId,
      signal,
    );

    if (!operationResult.success) {
      const details = resolveOperationErrorDetails(
        undefined,
        operationResult.errorData,
      );
      return {
        success: false,
        isWarning: false,
        operationId,
        details,
        error:
          details?.detail ||
          details?.title ||
          operationResult.error ||
          texts.createMachineFailedErrorMessage ||
          texts.skillsNotAvailableErrorMessage,
      };
    }

    const operation = operationResult.data;
    lastState = getOperationState(operation);
    const operationStatus = resolveOperationStatus(operation);

    if (lastState === "COMPLETED" || operationStatus === "success") {
      const machineId = operation?.result?.machine_id;
      if (machineId) {
        return { success: true, machineId };
      }

      return {
        success: false,
        isWarning: false,
        operationId,
        error:
          texts.createMachineFailedErrorMessage ||
          texts.skillsNotAvailableErrorMessage,
      };
    }

    if (operationStatus === "error") {
      const details = resolveOperationErrorDetails(operation);
      return {
        success: false,
        isWarning: false,
        operationId,
        details,
        error:
          operation?.detail?.trim() ||
          operation?.message?.trim() ||
          details?.detail?.trim() ||
          details?.title?.trim() ||
          resolveOperationFailureReason(operation) ||
          texts.createMachineFailedErrorMessage ||
          texts.skillsNotAvailableErrorMessage,
      };
    }

    if (attempt < OPERATION_POLL_MAX_ATTEMPTS - 1) {
      await waitForNextOperationPoll(signal);
    }
  }

  const isWarning = OPERATION_WARNING_STATES.includes(lastState);
  return {
    success: false,
    isWarning,
    operationId,
    error: isWarning
      ? texts.machineCreationTimeoutWarningMessage ||
        texts.operationPendingWarningMessage
      : texts.operationPendingWarningMessage,
  };
};

export const buildSkillPayload = (
  type: SkillType,
  hostname: string,
  opcUaSettings: OpcUaSettingsState,
  port?: number,
  rpcDsn?: string,
): CreateSkillRequestBody => {
  if (type === "VNC") {
    return {
      type: "VNC",
      enabled: true,
      host: hostname,
      port: port as number,
    };
  }

  if (type === "OPCUA") {
    const isUserLogin =
      opcUaSettings.authOption === DeviceAuthOption.USER_LOGIN;
    const hasClientCertificate = Boolean(
      opcUaSettings.clientCertificateBase64 &&
        opcUaSettings.clientCertificatePrivateKeyBase64,
    );
    const shouldIncludeCertificate = hasClientCertificate;

    return {
      type: "OPCUA",
      enabled: true,
      connection_details: {
        dsn: `opc.tcp://${hostname}:${port}`,
        auth_info: {
          auth_type: "OPCUA",
          security_mode: opcUaSettings.securityMode,
          security_policy: opcUaSettings.securityPolicy,
          username: isUserLogin ? opcUaSettings.username || null : null,
          password: isUserLogin ? opcUaSettings.password || null : null,
          client_certificate_base64: shouldIncludeCertificate
            ? opcUaSettings.clientCertificateBase64 || null
            : null,
          client_certificate_private_key_base64: shouldIncludeCertificate
            ? opcUaSettings.clientCertificatePrivateKeyBase64 || null
            : null,
          client_certificate_password: shouldIncludeCertificate
            ? opcUaSettings.clientCertificatePassword || null
            : null,
          server_public_key_base64: shouldIncludeCertificate
            ? opcUaSettings.serverPublicKeyBase64 || null
            : null,
        },
        reuse_last_autogenerated_client_certificate:
          opcUaSettings.reuseLastAutogeneratedClientCertificate,
      },
    };
  }

  return {
    type: "RPC",
    enabled: true,
    connection_details: {
      dsn: `rpc://${rpcDsn}`,
    },
  };
};
